import { useCallback, useMemo } from 'react';
import type { ModuleViewProps } from '../../shell/types';
import { ModulePage } from '../../shell/kit';
import { useTab } from '../../shell/hooks';
import { useToast } from '../../shell/toast';
import { FiscalTab } from './fiscal';
import { InvoicesTab } from './invoices';
import { makePaymentsT, type PaymentsKey } from './i18n';
import { ProvidersTab } from './providers';
import { ReconciliationTab } from './reconciliation';
import { TransactionsTab } from './transactions';

type TabKey = 'transactions' | 'invoices' | 'fiscal' | 'reconciliation' | 'providers';

const TABS: ReadonlyArray<{ key: TabKey; label: PaymentsKey }> = [
  { key: 'transactions', label: 'tab.transactions' },
  { key: 'invoices', label: 'tab.invoices' },
  { key: 'fiscal', label: 'tab.fiscal' },
  { key: 'reconciliation', label: 'tab.reconciliation' },
  { key: 'providers', label: 'tab.providers' },
];

export default function PaymentsModule({ title, user }: ModuleViewProps) {
  const locale = user.locale;
  const t = useMemo(() => makePaymentsT(locale), [locale]);
  const [tab, setTab] = useTab<TabKey>(
    'payments',
    TABS.map((item) => item.key),
    'transactions',
  );
  const toast = useToast();

  // Ответ показывается рядом с действием, а не плашкой вверху страницы.
  const onError = useCallback(
    (message: string) => {
      if (message) toast.error(message);
    },
    [toast],
  );

  return (
    <ModulePage
      title={title}
      subtitle={t('module.subtitle')}
      tabs={TABS.map((item) => ({ key: item.key, label: t(item.label) }))}
      activeTab={tab}
      onTab={setTab}
    >
      {tab === 'transactions' && <TransactionsTab t={t} onError={onError} />}
      {tab === 'invoices' && <InvoicesTab t={t} onError={onError} />}
      {tab === 'fiscal' && <FiscalTab t={t} onError={onError} />}
      {tab === 'reconciliation' && <ReconciliationTab t={t} onError={onError} />}
      {tab === 'providers' && <ProvidersTab t={t} onError={onError} />}
    </ModulePage>
  );
}
